<template>
  <e-form v-model="isValid" ref="formRef">
    <e-expansion-panels v-for="(group, groupIndex) in groups" :key="groupIndex" default-opened>
      <e-expansion-panel>
        <template #title>
          <div class="paragraph-l">
            {{ group.title }}
          </div>
        </template>

        <e-expansion-panels
          v-for="(subgroup, subgroupsIndex) in group.subgroups"
          :key="subgroupsIndex"
          default-opened
          class="mb-6"
        >
          <e-expansion-panel>
            <template #title>
              <div class="paragraph-m">
                {{ subgroup.title }}
              </div>
            </template>
            <e-data-items v-model="subgroup.selected" :headers="headers" :items="subgroup.items" return-object>
              <template v-slot:[`item.variant`]="{ value }">
                <span v-if="value.length <= 1">{{ value[0] }}</span>
                <help-tooltip v-else>
                  <div v-for="i in value" :key="i">
                    {{ i }}
                  </div>
                </help-tooltip>
              </template>

              <template v-slot:[`item.startDate`]="{ item, isSelected, internalItem }">
                <e-date-picker
                  v-if="item.startDate"
                  v-model="item.startDate"
                  clearable
                  :min="getIsoDateWithUtcOffset(new Date(), timeZoneOffsetMin)"
                  :max="item.endDate || max"
                  :rules="[
                    dateMoreOrEqual(
                      getIsoDateWithUtcOffset(new Date(), timeZoneOffsetMin),
                      'Не может быть меньше текущей даты',
                    ),
                    dateLessOrEqual(item.endDate),
                  ]"
                  :disabled="!isSelected(internalItem)"
                />
              </template>

              <template v-slot:[`item.endDate`]="{ item, isSelected, internalItem }">
                <e-date-picker
                  v-if="item.endDate"
                  v-model="item.endDate"
                  :min="item.startDate"
                  :max="max"
                  :rules="[dateMoreOrEqual(item.startDate)]"
                  :disabled="!isSelected(internalItem)"
                />
              </template>

              <template v-slot:[`item.tariff`]="{ item, isSelected, internalItem }">
                <div v-if="item.tariff === 'none'" class="text-link cursor-pointer" @click="item.tariff = 1">
                  Укажите значение
                </div>
                <e-input
                  v-else
                  v-model:number-value="item.tariff"
                  :key="String(isSelected(internalItem))"
                  :maska="quotaMask"
                  :rules="tariffRules(isSelected(internalItem))"
                  :disabled="subgroup.selected && !isSelected(internalItem)"
                  @update:number-value="onTariffUpdate(item.tariff)"
                />
              </template>
            </e-data-items>
          </e-expansion-panel>
        </e-expansion-panels>
      </e-expansion-panel>
    </e-expansion-panels>
  </e-form>
</template>

<script setup lang="ts">
import { useVModel } from '@vueuse/core';
import { computed, ref } from 'vue';
import type { DataTableHeaders } from '@/core/types/components/table';
import type { InitialValuesItem } from '@/parts/tariffs/types/tariffs';
import EDataItems from '@/core/components/common/data-items/EDataItems.vue';
import { EDatePicker, EExpansionPanel, EExpansionPanels, EForm, EInput, HelpTooltip } from '@/core/components/ui';
import { quotaMask } from '@/core/consts/masks';
import { useTourStore } from '@/core/store/tour';
import { getIsoDateWithUtcOffset } from '@/core/utils/date';
import { dateLessOrEqual, dateMoreOrEqual, min, required } from '@/core/utils/validation';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';

const props = defineProps<{
  isValid?: boolean;
  withDates?: boolean;
  max?: string;
}>();

defineEmits<{
  (event: 'update:isValid', value: boolean): void;
}>();

const { timeZoneOffsetMin } = useAccommodationObjectsStore();
const { setActiveTour } = useTourStore();

const formRef = ref();

const isValid = useVModel(props, 'isValid');

const groups = defineModel<InitialValuesItem[]>();

const tariffRules = (isSelected: boolean) => {
  const rules = [min(1)];

  if (isSelected) {
    rules.push(required());
  }

  return rules;
};

const onTariffUpdate = (value: number | null) => {
  if (value === 0) {
    setActiveTour('tariffZeroPriceNote');
  }
};

const headers = computed<DataTableHeaders>(() => [
  {
    key: 'name',
    title: 'Место',
  },
  {
    key: 'type',
    title: 'Тип туриста',
  },
  {
    key: 'variant',
    title: 'Вариант размещения',
    sortable: false,
  },
  {
    key: 'startDate',
    title: 'Период с',
    align: props.withDates ? 'end' : ' d-none',
    width: 180,
    sortable: false,
  },
  {
    key: 'endDate',
    title: 'Период по',
    align: props.withDates ? 'end' : ' d-none',
    width: 180,
    sortable: false,
  },
  {
    key: 'tariff',
    title: 'Цена',
    width: 200,
    sortable: false,
  },
]);

defineExpose({
  validate: () => formRef.value?.validate(),
});
</script>

<style scoped lang="scss"></style>
